import axios from 'axios';
import { API_BASE } from '../apiConfig';

const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send/receive refresh cookie
});

// Attach token on each request from storage (keeps in sync with refresh)
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let refreshPromise = null;

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${API_BASE}/auth/refresh`,
        {},
        { withCredentials: true }
      )
      .then((res) => res.data?.data || null)
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  const refreshed = await refreshPromise;
  if (!refreshed?.token) return null;

  const { token, user } = refreshed;
  localStorage.setItem('token', token);
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  }
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  return token;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config || {};

    const isAuthEndpoint =
      typeof originalRequest.url === 'string' &&
      (originalRequest.url.includes('/auth/login') ||
       originalRequest.url.includes('/auth/register') ||
       originalRequest.url.includes('/auth/refresh'));

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return axiosInstance(originalRequest);
      }

      // Refresh failed; clear client auth state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axiosInstance.defaults.headers.common.Authorization;
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
